import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Clock, Users, Copy, Check, ExternalLink, Star } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../components/ui/carousel";
import { API, setMeta, fmtErr } from "./api";
import { T, TTitle, TBadge, THeader, TFooter, YtEmbed } from "./ui";

// Bannière tournante des autres villes actives (carrousel auto 5 s, flèches, tactile)
function BannerVilles({ villes, currentId }) {
  const autres = (villes || []).filter((v) => v.id !== currentId);
  if (autres.length === 0) return null;
  return (
    <div data-testid="tournee-banner" className="bg-forest text-ivory py-4 px-6">
      <Carousel opts={{ align: "start", loop: autres.length > 2 }} plugins={[Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })]} className="max-w-4xl mx-auto">
        <CarouselContent>
          {autres.map((v) => (
            <CarouselItem key={v.id} data-testid={`banner-ville-${v.slug}`} className="basis-full sm:basis-1/2 lg:basis-1/3">
              <Link to={`/tournee/${v.slug}`} className="block text-center px-4 py-2 group">
                <p className="font-serif text-lg text-ivory group-hover:text-gold transition-colors">{v.nom} <span className="text-whisper/60 text-sm">· {v.pays}</span></p>
                <p className="text-[11px] tracking-[0.16em] uppercase text-whisper/70">{v.dates || "Dates à venir"} — {v.statut || "étape"}</p>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious data-testid="banner-prev" className="hidden sm:flex -left-2 border-ivory/30 bg-transparent text-ivory hover:bg-ivory/10" />
        <CarouselNext data-testid="banner-next" className="hidden sm:flex -right-2 border-ivory/30 bg-transparent text-ivory hover:bg-ivory/10" />
      </Carousel>
    </div>
  );
}

function OffreCard({ offre, tidycalGlobal, villeId, index }) {
  const complet = String(offre.places_restantes || "").trim() === "0";
  const link = offre.tidycal_url || tidycalGlobal;
  return (
    <article data-testid={`offre-card-${index}`} className={`${T.card} flex flex-col h-full`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <span className="text-[10px] tracking-[0.28em] uppercase text-gold">{offre.jour}</span>
        {offre.badge && <TBadge>{offre.badge}</TBadge>}
      </div>
      <h3 className="font-serif text-2xl text-ink dark:text-whisper leading-tight">{offre.titre}</h3>
      {offre.description && <p className="mt-3 text-sm text-stone dark:text-mist leading-relaxed">{offre.description}</p>}
      <ul className="mt-4 space-y-1.5 text-xs text-stone dark:text-mist">
        {offre.horaire && <li className="flex items-center gap-2"><Calendar size={13} className="text-forest dark:text-gold" /> {offre.horaire}</li>}
        {offre.duree && <li className="flex items-center gap-2"><Clock size={13} className="text-forest dark:text-gold" /> {offre.duree}</li>}
        {(offre.adresse) && <li className="flex items-center gap-2"><MapPin size={13} className="text-forest dark:text-gold" /> {offre.adresse}</li>}
        {String(offre.places_restantes || "") !== "" && (
          <li className="flex items-center gap-2"><Users size={13} className="text-forest dark:text-gold" /> {complet ? "Complet" : `${offre.places_restantes} place${Number(offre.places_restantes) > 1 ? "s" : ""} restante${Number(offre.places_restantes) > 1 ? "s" : ""}`}</li>
        )}
      </ul>
      {offre.prix_durees && Object.keys(offre.prix_durees).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(offre.prix_durees).map(([min, prix]) => prix && (
            <span key={min} className="text-[10px] tracking-[0.12em] uppercase border border-beige dark:border-charcoal rounded-full px-3 py-1.5 text-stone dark:text-mist">{min} min — {prix}</span>
          ))}
        </div>
      )}
      <div className="mt-auto pt-6">
        {offre.early_bird && <p className="text-xs text-stone dark:text-mist line-through">{offre.prix}</p>}
        <p className="font-serif text-3xl text-forest dark:text-gold">{offre.early_bird || offre.prix || "Prix à venir"}</p>
        {offre.early_bird && <p className="text-[10px] tracking-[0.18em] uppercase text-gold mt-0.5">Early bird · puis {offre.prix}</p>}
        {complet ? (
          <span data-testid={`offre-complet-${index}`} className={`${T.outline} w-full mt-4 opacity-50 cursor-not-allowed`}>Complet</span>
        ) : (
          <a data-testid={`offre-reserver-${index}`} href={link} target="_blank" rel="noopener noreferrer" className={`${T.forest} w-full mt-4`}>
            Réserver via Tidycal <ExternalLink size={13} />
          </a>
        )}
      </div>
    </article>
  );
}

export default function TourneeVille() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [stEmail, setStEmail] = useState({ state: "idle", msg: "" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    axios.get(`${API}/ville/${slug}`)
      .then(({ data }) => {
        setData(data);
        setMeta(
          `La Tournée Le Guérisseur à ${data.ville.nom} — Soins, Rencontres & Formation`,
          `Étape de la Tournée Le Guérisseur à ${data.ville.nom} : ${data.ville.dates || "dates à venir"}. Soin LE GUÉRISSEUR, soirée de rencontre et journée collective. Réservation via Tidycal.`
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col">
        <THeader />
        <div className="flex-1 flex items-center justify-center text-center px-6 py-24">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink dark:text-whisper" data-testid="tournee-404">Cette étape n'est pas encore ouverte</h1>
            <p className="mt-4 text-stone dark:text-mist">La tournée se prépare. Propose ta ville pour qu'elle devienne une étape.</p>
            <Link data-testid="tournee-404-back" to="/tournee" className={`${T.forest} mt-8`}>Découvrir la tournée <ArrowRight size={14} /></Link>
          </div>
        </div>
        <TFooter settings={{}} />
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-stone dark:text-mist">Chargement…</div>;
  }

  const { ville, offres, programme, videos, temoignages, faq, autres_villes, settings } = data;
  const tidycal = settings.tidycal_url;
  const vir = settings.virement || {};
  const banner = settings.banner_enabled && <BannerVilles villes={autres_villes} currentId={ville.id} />;
  const infos = [
    ["Dates", ville.dates], ["Adresse", ville.adresse], ["Lieu", ville.lieu_nom],
    ["Accès", ville.acces], ["Parking", ville.parking], ["À apporter", ville.a_apporter],
    ["Annulation", ville.annulation], ["Contact", ville.contact],
  ].filter(([, v]) => v);

  const submitEmail = async (e, offreId = null) => {
    e.preventDefault();
    setStEmail({ state: "sending", msg: "" });
    try {
      await axios.post(`${API}/inscriptions`, { ville_id: ville.id, email, offre_id: offreId });
      setStEmail({ state: "ok", msg: "Inscription enregistrée ! Regarde ta boîte mail pour la confirmation." });
      setEmail("");
    } catch (err) {
      setStEmail({ state: "err", msg: fmtErr(err) });
    }
  };

  return (
    <div data-testid="tournee-ville-page" className="bg-ivory/60 dark:bg-night/60 min-h-screen">
      <THeader right={
        <a data-testid="ville-cta-tidycal-top" href={ville.tidycal_url || tidycal} target="_blank" rel="noopener noreferrer" className={T.gold}>
          Réserver via Tidycal
        </a>
      } />

      {settings.banner_position !== "bas" && banner}

      {/* 3 — HERO */}
      <section data-testid="ville-hero" className="py-20 sm:py-28 text-center px-6">
        <div className="max-w-3xl mx-auto">
          <TBadge testid="ville-badge">{ville.statut || "Étape confirmée"}</TBadge>
          <h1 data-testid="ville-h1" className="mt-8 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-ink dark:text-whisper">
            La Tournée <span className="text-forest dark:text-gold">LE GUÉRISSEUR</span> arrive à {ville.nom}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone dark:text-mist">
            {ville.dates && <span className="block">{ville.dates}</span>}
            {(ville.lieu_nom || ville.adresse) && <span className="block mt-1 text-sm">{ville.lieu_nom}{ville.lieu_nom && ville.adresse ? " — " : ""}{ville.adresse}</span>}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a data-testid="ville-cta-tidycal" href={ville.tidycal_url || tidycal} target="_blank" rel="noopener noreferrer" className={T.forest}>
              Réserver via Tidycal <ExternalLink size={14} />
            </a>
          </div>
          {/* Champ email */}
          <form data-testid="ville-email-form" onSubmit={(e) => submitEmail(e)} className="mt-8 max-w-md mx-auto flex gap-2">
            <input data-testid="ville-email-input" type="email" required placeholder="Ton email pour être informé·e" value={email} onChange={(e) => setEmail(e.target.value)} className={T.input} />
            <button data-testid="ville-email-submit" type="submit" disabled={stEmail.state === "sending"} className={`${T.gold} shrink-0 !px-6`}>
              {stEmail.state === "sending" ? "…" : "OK"}
            </button>
          </form>
          {stEmail.state === "ok" && <p data-testid="ville-email-success" className="mt-3 text-sm text-forest dark:text-gold">{stEmail.msg}</p>}
          {stEmail.state === "err" && <p data-testid="ville-email-error" className="mt-3 text-sm text-red-700 dark:text-red-400">{stEmail.msg}</p>}
        </div>
      </section>

      {/* 4 — POURQUOI CETTE ÉTAPE */}
      {ville.texte_pourquoi && (
        <section data-testid="ville-pourquoi" className="py-16 sm:py-24 bg-sand/70 dark:bg-onyx/70">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <TTitle eyebrow="L'étape" title="Pourquoi cette étape" />
            <p className="text-base sm:text-lg text-stone dark:text-mist leading-relaxed whitespace-pre-wrap">{ville.texte_pourquoi}</p>
          </div>
        </section>
      )}

      {/* 5 — CE QUE TU PEUX VIVRE (offres) */}
      {offres.length > 0 && (
        <section data-testid="ville-offres" className="py-16 sm:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <TTitle eyebrow="Le week-end" title="Ce que tu peux vivre" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {offres.map((o, i) => <OffreCard key={o.id} offre={o} tidycalGlobal={tidycal} villeId={ville.id} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* 6 — PROGRAMME DU DIMANCHE */}
      {programme.length > 0 && (
        <section data-testid="ville-programme" className="py-16 sm:py-24 bg-sand/70 dark:bg-onyx/70">
          <div className="max-w-3xl mx-auto px-6">
            <TTitle eyebrow="Dimanche" title="Programme de la journée" />
            <ol className="border-t border-beige dark:border-charcoal">
              {programme.map((p, i) => (
                <li key={p.id} data-testid={`programme-item-${i}`} className="grid sm:grid-cols-[120px_1fr] gap-x-6 gap-y-1 border-b border-beige dark:border-charcoal py-5">
                  <span className="font-serif text-xl text-forest dark:text-gold">{p.heure}</span>
                  <span>
                    <span className="block font-medium text-ink dark:text-whisper">{p.titre}</span>
                    {p.description && <span className="block text-sm text-stone dark:text-mist mt-1 leading-relaxed">{p.description}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* 7 — POUR QUI */}
      {ville.texte_pour_qui && (
        <section data-testid="ville-pour-qui" className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <TTitle eyebrow="Participants" title="Pour qui ?" />
            <p className="text-base sm:text-lg text-stone dark:text-mist leading-relaxed whitespace-pre-wrap">{ville.texte_pour_qui}</p>
          </div>
        </section>
      )}

      {/* 8 — CE QUE TU REPARS AVEC */}
      {ville.texte_repars && (
        <section data-testid="ville-repars" className="py-16 sm:py-24 bg-sand/70 dark:bg-onyx/70">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <TTitle eyebrow="Les fruits" title="Ce que tu repars avec" />
            <p className="text-base sm:text-lg text-stone dark:text-mist leading-relaxed whitespace-pre-wrap">{ville.texte_repars}</p>
          </div>
        </section>
      )}

      {/* 9 — VIDÉOS (max 3) */}
      {settings.show_videos && videos.length > 0 && (
        <section data-testid="ville-videos" className="py-16 sm:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <TTitle eyebrow="En images" title="Vidéos" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {videos.slice(0, 3).map((v, i) => (
                <div key={v.id}>
                  <YtEmbed url={v.youtube_url} title={v.titre} testid={`ville-video-${i}`} />
                  {v.titre && <p className="mt-3 font-serif text-lg text-ink dark:text-whisper text-center">{v.titre}</p>}
                  {v.description && <p className="mt-1 text-xs text-stone dark:text-mist text-center">{v.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 10 — TÉMOIGNAGES */}
      {temoignages.length > 0 && (
        <section data-testid="ville-temoignages" className="py-16 sm:py-24 bg-sand/70 dark:bg-onyx/70">
          <div className="max-w-5xl mx-auto px-6">
            <TTitle eyebrow="Ils l'ont vécu" title="Témoignages" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {temoignages.map((t, i) => (
                <figure key={t.id} data-testid={`ville-temoignage-${i}`} className={`${T.card} flex flex-col`}>
                  {t.note > 0 && (
                    <div className="flex gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={13} className={s <= t.note ? "text-gold fill-gold" : "text-stone/30"} strokeWidth={1.5} />)}
                    </div>
                  )}
                  <blockquote className="text-sm text-stone dark:text-mist leading-relaxed flex-1 whitespace-pre-wrap">{t.texte}</blockquote>
                  <figcaption className="mt-4 pt-3 border-t border-beige dark:border-charcoal font-serif text-lg text-ink dark:text-whisper">
                    {t.auteur} {t.ville && <span className="block text-xs font-sans text-stone dark:text-mist">{t.ville}</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 11 — TARIFS (générés depuis les offres) */}
      {offres.length > 0 && (
        <section data-testid="ville-tarifs" className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <TTitle eyebrow="Tarifs" title="Les tarifs de l'étape" />
            <ol className="border-t border-beige dark:border-charcoal">
              {offres.map((o, i) => (
                <li key={o.id} data-testid={`tarif-row-${i}`} className="flex items-baseline justify-between gap-4 border-b border-beige dark:border-charcoal py-4">
                  <span className="text-sm sm:text-base text-ink dark:text-whisper"><span className="text-[10px] tracking-[0.2em] uppercase text-gold mr-2">{o.jour}</span>{o.titre}</span>
                  <span className="font-serif text-xl text-forest dark:text-gold whitespace-nowrap">{o.early_bird ? `${o.early_bird}*` : (o.prix || "—")}</span>
                </li>
              ))}
            </ol>
            {offres.some((o) => o.early_bird) && <p className="mt-3 text-xs text-stone dark:text-mist">* Tarif early bird, ensuite tarif standard.</p>}
          </div>
        </section>
      )}

      {/* 12 — INFORMATIONS PRATIQUES */}
      <section data-testid="ville-infos" className="py-16 sm:py-24 bg-sand/70 dark:bg-onyx/70">
        <div className="max-w-4xl mx-auto px-6">
          <TTitle eyebrow="Venir" title="Informations pratiques" />
          <div className="grid sm:grid-cols-2 gap-4">
            {infos.map(([label, value]) => (
              <div key={label} className={`${T.card} !p-5`}>
                <p className="text-[10px] tracking-[0.24em] uppercase text-gold mb-1.5">{label}</p>
                <p className="text-sm text-ink dark:text-whisper leading-relaxed whitespace-pre-wrap">{value}</p>
              </div>
            ))}
            {ville.maps_url && (
              <div className={`${T.card} !p-5`}>
                <p className="text-[10px] tracking-[0.24em] uppercase text-gold mb-1.5">Google Maps</p>
                <a data-testid="ville-maps-link" href={ville.maps_url} target="_blank" rel="noopener noreferrer" className="text-sm text-forest dark:text-gold inline-flex items-center gap-1.5 link-underline">
                  Ouvrir dans Maps <ExternalLink size={12} />
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 13 — PAIEMENT */}
      <section data-testid="ville-paiement" className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <TTitle eyebrow="Réservation" title="Paiement" lead="La réservation se fait via Tidycal. Le virement bancaire est également accepté." />
          <a data-testid="ville-paiement-tidycal" href={ville.tidycal_url || tidycal} target="_blank" rel="noopener noreferrer" className={T.forest}>
            Réserver via Tidycal <ExternalLink size={14} />
          </a>
          {vir.active && vir.iban && (
            <div data-testid="ville-virement" className={`${T.card} mt-8 text-left`}>
              <p className="text-[10px] tracking-[0.24em] uppercase text-gold mb-4 text-center">Paiement par virement bancaire</p>
              <div className="space-y-2 text-sm text-stone dark:text-mist">
                {vir.titulaire && <p><span className="text-ink dark:text-whisper">Titulaire :</span> {vir.titulaire}</p>}
                {vir.banque && <p><span className="text-ink dark:text-whisper">Banque :</span> {vir.banque}</p>}
                {vir.bic && <p><span className="text-ink dark:text-whisper">BIC :</span> {vir.bic}</p>}
                {vir.reference && <p><span className="text-ink dark:text-whisper">Référence à indiquer :</span> {vir.reference}</p>}
              </div>
              <div className="mt-4 flex flex-col sm:flex-row items-center gap-3">
                <code data-testid="ville-iban" className="flex-1 w-full rounded-xl border border-beige dark:border-charcoal bg-ivory/80 dark:bg-night/80 px-4 py-3 text-sm text-ink dark:text-whisper tracking-wider break-all">{vir.iban}</code>
                <button
                  data-testid="ville-copy-iban"
                  onClick={() => { navigator.clipboard?.writeText(vir.iban); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className={`${T.outline} !px-6 shrink-0`}
                >
                  {copied ? <><Check size={13} /> Copié !</> : <><Copy size={13} /> Copier l'IBAN</>}
                </button>
              </div>
              {vir.instructions && <p className="mt-4 text-xs text-stone dark:text-mist leading-relaxed">{vir.instructions}</p>}
            </div>
          )}
        </div>
      </section>

      {/* 14 — FAQ */}
      {faq.length > 0 && (
        <section data-testid="ville-faq" className="py-16 sm:py-24 bg-sand/70 dark:bg-onyx/70">
          <div className="max-w-3xl mx-auto px-6">
            <TTitle eyebrow="Questions" title="FAQ" />
            <div className="space-y-3">
              {faq.map((f, i) => (
                <details key={f.id} data-testid={`ville-faq-${i}`} className={`${T.card} !p-5 group`}>
                  <summary className="cursor-pointer font-medium text-ink dark:text-whisper list-none flex items-center justify-between gap-4">
                    {f.question}
                    <ArrowRight size={14} className="text-gold shrink-0 transition-transform duration-300 group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm text-stone dark:text-mist leading-relaxed whitespace-pre-wrap">{f.reponse}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 15 — CTA FINAL */}
      <section data-testid="ville-cta-final" className="py-20 sm:py-28 text-center px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink dark:text-whisper">Réserve ta place à {ville.nom}</h2>
          <p className="mt-4 text-stone dark:text-mist">{ville.dates}</p>
          <a data-testid="ville-final-tidycal" href={ville.tidycal_url || tidycal} target="_blank" rel="noopener noreferrer" className={`${T.gold} mt-8`}>
            Réserver via Tidycal <ExternalLink size={14} />
          </a>
        </div>
      </section>

      {settings.banner_position === "bas" && banner}

      <TFooter settings={settings} />
    </div>
  );
}
